import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "../AwesomeIcon";
import classNames from "classnames";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";

interface DebounceInputProps {
  value?: string | number;
  onChange?: (arg: any) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  inputClassName?: string;
  onFocus?: (arg: any) => void;
  onBlur?: (arg: any) => void;
  LHSIcon?: JSX.Element;
  RHSIcon?: JSX.Element;
  closeIcon?: JSX.Element;
  hasClear?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const DebounceInput = (props: DebounceInputProps) => {
  const {
    value,
    onChange,
    placeholder,
    className = "",
    inputClassName = "",
    disabled = false,
    onFocus,
    onBlur,
    LHSIcon,
    RHSIcon,
    hasClear,
    closeIcon,
    onKeyDown,
  } = props;
  const [text, setText] = useState<string | number | undefined>("");

  useEffect(() => {
    if (value != text) {
      setText(value);
    }
  }, [value]);

  const debounce = <T,>(func: (...args: T[]) => void) => {
    let timer: NodeJS.Timeout | null;
    return (...args: T[]) => {
      const context = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(context, args);
      }, 500);
    };
  };

  const handleChange = (val: string) => {
    setText(val);
    optimizedFn(val);
  };

  const optimizedFn = useCallback(
    debounce((val) => {
      onChange ? onChange(val) : null;
    }),
    []
  );

  return (
    <label
      className={classNames("relative text-gray-500 dark:text-gray-400 block w-full", {
        [className]: !!className,
      })}
    >
      {LHSIcon}
      <input
        value={text}
        className={classNames(
          "form-input w-full p-3 pl-8 pr-8 bg-gray-200 dark:bg-gray-800 rounded-md border-transparent focus:border-transparent text-md",
          {
            " !border-teal-300": !_.isEmpty(text),
            [inputClassName]: !!inputClassName,
            "!border-teal-500 dark:!border-teal-300":
              !_.isEmpty(text) && !!inputClassName,
          }
        )}
        placeholder={placeholder}
        title={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
      {hasClear && !!text && (
        <div
          className="flex items-center justify-center h-full w-6 absolute top-1/2 transform -translate-y-1/2 right-3 cursor-pointer !text-gray-400 dark:text-gray-600"
          onClick={(e) => {
            handleChange("");
            e.stopPropagation();
          }}
        >
          <AwesomeIcon name={faXmark} size="lg" className={classNames("")} />
        </div>
      )}
      {closeIcon}
      {RHSIcon}
    </label>
  );
};

export default DebounceInput;
